<template>
<div class="container-fluid">
  <div class="row">
    <div class="col-12">
            
      <div class="d-flex align-items-center justify-content-between">
        <h1 data-test="pageTitle">All Groups</h1>
        <span
          v-if="perms.can_create_group"
          v-tooltip.bottom="'Add Group'"
          class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
          data-toggle="modal" data-target="#groupCreateModal"
          data-test="addGroupButton">
          <i class="far fa-plus-circle text-xl"></i>
        </span>
      </div>

      <div class="d-flex justify-content-between">
        <filter-bar
          filter_type="specialty"
          @filter-set="setFilter($event)"
          @filter-specialty-select="selectSpecialtyFilter($event)"
          @filter-reset="resetAllFilters()"
        />
          
        <div class="dropdown">
          <button
            type="button"
            class="btn btn-block leading-none dropdown-toggle"
            :class="selectedGroups.length < 2 ? 'btn-light' : 'btn-primary'"
            :disabled="selectedGroups.length < 2"
            data-toggle="dropdown"
            data-test="addToSelectorButton">
            Add to...
          </button>
          <div class="dropdown-menu dropdown-menu-right py-0">
            <span
              class="dropdown-item d-flex align-items-baseline"
              @click="setRouteVisits()"
              data-toggle="modal" data-target="#routeCreateModal" data-test="addToRouteButton"
            >
              <div class="mr-3">
                <i class="fal fa-route mr-1"></i> New Route
              </div>
              <span class="badge badge-primary">{{ routeStops.length }}</span>
            </span>
            <span
              class="dropdown-item d-flex align-items-baseline"
              @click="setCampaignVisits()"
              data-toggle="modal" data-target="#campaignCreateModal" data-test="addToCampaignButton"
            >
              <div class="mr-3">
                <i class="fal fa-fire-alt mr-1"></i> New Campaign
              </div>
              <span class="badge badge-primary">{{ selectedGroups.length }}</span>
            </span>
          </div>
        </div>
      </div>

    <div class="card leading-tight">
      <vuetable ref="vuetable"
        api-url="/api/groups/groups_paged"
        :fields="fields"
        data-path="data"
        pagination-path=""
        :css="css.table"
        :sort-order="sortOrder"
        :sort-params="getSortParam"
        :multi-sort="false"
        :append-params="moreParams"
        :no-data-template="emptyText"
        @vuetable:load-success="onDataLoaded"
        @vuetable:pagination-data="onPaginationData">

        <div slot="name" slot-scope="props" class="d-flex align-items-center">
          <input class="mr-1" type="checkbox" v-model="selectedGroups" :value="props.rowData.id" >

          <div class="rounded-circle circle-cropped avatar-sm mr-2 font-weight-bold text-sm text-center">
            <img v-if="props.rowData.photo_path" :src="props.rowData.photo_url" class="img-fluid">
            <div v-else class="bg-gray-200 text-gray-600 border" style="">{{ props.rowData.initials }}</div>
          </div>

          <div>
            <div>
              <router-link :to="{ name: 'groups.show', params:{ id:props.rowData.id } }" data-test="viewGroupLink">
                {{ props.rowData.name }}
              </router-link>
              <i v-if="!props.rowData.location_primary"
                class="far fa-map-marker-alt-slash text-sm text-gray-500 ml-1"
                v-tooltip.bottom="'No location'" />   
            </div>
            <div>
              <span
                v-for="specialty in props.rowData.specialties"
                class="badge bg-teal-200 text-teal-700 mr-1"
                v-html="specialty.label"
              />
            </div>
          </div>
        </div>

        <template slot="location_primary" slot-scope="props" v-if="props.rowData.location_primary">
          {{ props.rowData.location_primary.city }}{{ props.rowData.location_primary.state ? ', ' + props.rowData.location_primary.state : '' }}
        </template>

        <template slot="last_checkin_id" slot-scope="props" v-if="props.rowData.last_checkin_id">
          <span class="badge" :class="props.rowData.last_checkin.checkin_type.css_class_name">{{ props.rowData.last_checkin.checkin_type.label }}</span>
          <div class="mt-1 text-sm text-gray-600 leading-none">{{ moment(props.rowData.last_checkin.created_at).format('MMM D, YYYY') }}</div>          
        </template>

        <template slot="created_at" slot-scope="props">
          <div class="text-gray-600 text-sm">
            {{ moment(props.rowData.created_at).format('MMM D, YYYY') }}
            <div v-if="props.rowData.user" class="text-sm text-gray-600 mt-1 leading-none">by {{ props.rowData.user.display_name }}</div>
            <div v-else class="mt-1 leading-none"><em>by admin</em></div>
          </div>
        </template>

        <template slot="actions" slot-scope="props">
          <div class="drop-down dropleft">
            <a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown">
                <i class="far fa-ellipsis-h text-xl"></i>
            </a>
            <div class="dropdown-menu py-0" aria-labelledby="dropdownMenuLink">
              <router-link :to="{ name: 'groups.show', params:{ id:props.rowData.id } }" class="dropdown-item" data-test="providerProfileLink">View Profile</router-link>
              <span class="dropdown-item" @click="buildCheckinModal(props.rowData.id, props.rowData.name)" data-toggle="modal" data-target="#myModal">Add Check-in</span>
            </div>
          </div>
        </template>        
      </vuetable>
    </div>

    <div class="background-fix vuetable-pagination d-flex mb-3">
      <vuetable-pagination-info
        ref="paginationInfo"
        class="flex-fill"
        info-class="pagination-info"
        info-template="{from} to {to} of {total} items" />

      <vuetable-pagination-bootstrap
        ref="pagination"
        :css="css.pagination"
        class="flex-fill text-right"
        @vuetable-pagination:change-page="onChangePage" />
    </div>


  </div>
</div>
<campaign-create-modal />
<group-create-modal />
</div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';
  import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
  import CampaignCreateModal from '../../components/CampaignCreateModal';
  import FilterBar from '../../components/FilterBar';
  import GroupCreateModal from '../../components/GroupCreateModal';
  import Vuetable from 'vuetable-2';
  import VuetablePaginationInfo from 'vuetable-2/src/components/VuetablePaginationInfo';
  import VuetablePaginationBootstrap from '../../components/VuetablePaginationBootstrap.vue';

  export default {
    mixins: [BreadcrumbsManager],

    components: {
      CampaignCreateModal,      
      FilterBar,
      GroupCreateModal,
      Vuetable,
      VuetablePaginationInfo,
      VuetablePaginationBootstrap,
    },

    data() {
      return {
        //api_url: '/api/groups/groups_paged',
        emptyText: 'Loading...please wait',

        fields: [           
          {
            name: 'name',
            title: 'Name',
            sortField: 'name',
          },
          { 
            name: 'location_primary',
            title: 'Location',
            titleClass: 'hidden_mobile',
            dataClass: 'hidden_mobile',
          },
          {
            name: 'last_checkin_id',
            title: 'Last Check-in',
            sortField: 'last_checkin_id',
          },
          {
            name: 'physicians_count',
            title: '# Physicians',
            sortField: 'physicians_count'
          },
          {
            name: 'created_at',
            title: 'Added',
            sortField: 'created_at',
            titleClass: 'd-none d-md-table-cell',
            dataClass: 'd-none d-md-table-cell',
          },          
          {
            name: 'actions',
            title: '',
          },
        ],

        css: {
          table: {
            tableClass: 'table groups-table',
            ascendingIcon: 'fas fa-sort-alpha-down',
            descendingIcon: 'fas fa-sort-alpha-up'
          },

          pagination: {
            wrapperClass: 'pagination',
            activeClass: 'active',
            disabledClass: 'disabled',
            pageClass: 'page',
            linkClass: 'link',
            icons: {
              first: '',
              prev: '',
              next: '',
              last: '',
            },
          },

          icons: {
            first: 'fas fa-angle-double-left',
            prev: 'fas fa-angle-left',
            next: 'fas fa-angle-right',
            last: 'fas fa-angle-double-right',
          },
        },

        sortOrder: [
          {
            field: 'name',
            direction: 'asc',
          }
        ],

        moreParams: {
        },

        checkin_options: {
        },

        selectedGroups: [],
      };
    },

    created() {
    },

    activated() {
      this.setBreadcrumbs([
          { text: 'Home', to: { name: 'home' } },
          { text: 'Groups', to: { name: 'groups.list' } },
      ]); 
      this.$refs.vuetable.refresh();
    },

    mounted() {
      this.$root.$on('providers_changed', data => {
        this.$refs.vuetable.refresh();
      });
    },

    methods: {
      buildCheckinModal(group_id, name) {
        this.checkin_options.checkinables = {
          morphable_id : group_id,
          morphable_type : 'App\\Group',
          display_name : name,
        };

        this.$root.$emit('build_checkin_modal', { checkin_options: this.checkin_options });
      },

      getSortParam: function(sortOrder) {
        return sortOrder.map(function(sort) {
          return (sort.direction === 'desc' ? '-' : '') + sort.field
        }).join(',')
      },

      onPaginationData (paginationData) {
        this.$refs.pagination.setPaginationData(paginationData)
        this.$refs.paginationInfo.setPaginationData(paginationData)
      },

      onChangePage (page) {
        this.$refs.vuetable.changePage(page)
      },

      selectSpecialtyFilter(filterSpecialtyId) {
        this.moreParams = {
          'filter[has_specialties]': filterSpecialtyId
        }
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },

      setFilter(filterTextName) {
        this.moreParams = {
          'filter[search_full_name]': filterTextName
        }
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },
      
      resetAllFilters() {
        this.moreParams = {}
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },

      onDataLoaded() {
        Vue.nextTick( () => {
          if (this.$refs.vuetable.tableData.length == 0) {
            this.emptyText = 'No records found';
          }
        });          
      },

      setCampaignVisits() {
        this.$root.$emit('build_campaign_modal', { 
          campaign_stops: this.allGroupIds.filter(provider => this.selectedGroups.includes(provider.id)),
          save_groups_first: false
        });
      }, 

      setRouteVisits() {
        this.$root.$emit('build_route_modal', { 
          route_stops: this.routeStops,
          save_groups_first: false
        });
      }, 
    },

    computed: {
      ...mapGetters(['allGroupIds', 'mappableGroupIds']),      
      ...mapState(['perms']),

      routeStops() {
        return this.mappableGroupIds.filter(provider => this.selectedGroups.includes(provider.id));
      }
    },
  }
</script>
<style scoped>
tr:hover .drop-down { 
    opacity:1;
    transition:.2s all ease-out;
  }
</style>